// src/api/snapshot.ts
import { API_BASE, buildHeaders, fetchWithAuth } from "../utils/api";
import { get as storeGet, put as storePut, clearAll as storeClearAll } from "../utils/store";
import type { ScheduleEntry } from "../utils/schedule";

export type SnapshotEnrollment = {
  course_id: number;
  current_day: number;
  streak: number;
  best_streak: number;
  xp: number;
  started_at: string;
  last_complete_at: string | null;
  tz_at_start: string;
  morning_hour_effective: number;
  morning_minute_effective: number;
  // OF-PKG1A: расписание окна (сервер OF-PKG0). Необязательное — у снапшота
  // из старого кэша поля нет; undefined трактуется как null (utils/schedule.ts).
  schedule?: ScheduleEntry[] | null;
};

// totalEarned — camelCase-исключение в иначе snake_case контракте (сверено curl'ом 22.07.2026)
export type SnapshotSparks = {
  saas_client_id: string;
  balance: number;
  totalEarned: number;
};

export type SnapshotProfile = {
  timezone: string | null;
  pwa_installed: boolean;
};

export type AccessSnapshot = {
  ok: boolean;
  user_id: number;
  generated_at: string;
  profile: SnapshotProfile;
  courses: number[];
  enrollments: SnapshotEnrollment[];
  sparks: SnapshotSparks[];
};

// M5-STEP1 (15.09.2026): трёхзначный результат вместо AccessSnapshot|null —
// "empty" и "unavailable" были неразличимы (SILENT-ERRORS, находка E).
// 401 — не unavailable: сессия уже обработана в fetchWithAuth (clearSession +
// редирект на /unauth), см. mira_changelog.txt, «УТОЧНЕНИЯ К РЕШЕНИЮ 7», 7.1.
// M5-STORE1 (15.09.2026): добавлен четвёртый вариант — "cached". Раньше
// "unavailable" было терминальным отказом; теперь перед ним есть шанс отдать
// последний известный снапшот из IndexedDB (content-access, инвариант 9 —
// отзыв доступа действует на выдачу, не на уже выданное: запись в кэше могла
// быть сделана ДО отзыва и переживает его до истечения грейса STALE_AFTER_MS).
// cachedAt/stale едут вместе с данными — понадобятся индикатору синхронизации
// позже (не в этом заходе, решение владельца).
export type SnapshotResult =
  | { status: "ok"; data: AccessSnapshot }
  | { status: "empty" } // сервер ответил, данных нет (включая уже обработанный 401)
  | { status: "unavailable" } // спросить не удалось, и в кэше тоже пусто
  | { status: "cached"; data: AccessSnapshot; cachedAt: number; stale: boolean }; // спросить не удалось, но есть последний известный снимок

const SNAPSHOT_CACHE_KEY = "access_snapshot";

/** Спросить кэш при сетевом отказе. Пусто в кэше → unavailable, как раньше. */
async function cachedOrUnavailable(): Promise<SnapshotResult> {
  const cached = await storeGet<AccessSnapshot>(SNAPSHOT_CACHE_KEY);
  if (cached.status === "empty") return { status: "unavailable" };
  console.error(`[snapshot] served from cache, age=${Date.now() - cached.cachedAt}`);
  return {
    status: "cached",
    data: cached.data,
    cachedAt: cached.cachedAt,
    stale: cached.status === "stale",
  };
}

/** Положить успешный ответ в кэш. Смена пользователя на устройстве — clearAll() до записи новых данных (чужой снимок не должен пережить чужой вход). */
async function cacheSnapshot(data: AccessSnapshot): Promise<void> {
  const existing = await storeGet<AccessSnapshot>(SNAPSHOT_CACHE_KEY);
  if (existing.status !== "empty" && existing.userId !== data.user_id) {
    console.error("[snapshot] cached user differs from current, clearing store before save");
    await storeClearAll();
  }
  await storePut(SNAPSHOT_CACHE_KEY, data, data.user_id);
}

export async function getSnapshot(): Promise<SnapshotResult> {
  let res: Response;
  try {
    res = await fetchWithAuth(`${API_BASE}/api/access/snapshot`, {
      headers: buildHeaders(false),
    });
  } catch (e) {
    console.error("[snapshot] network error:", e instanceof Error ? e.message : String(e));
    return cachedOrUnavailable();
  }

  if (res.status === 401) {
    // Легитимный "не авторизован" — не отказ спросить. Редирект на /unauth
    // уже запущен внутри fetchWithAuth к этому моменту (уточнение 7.1).
    // В кэш не заглядываем — сервер ответил, данных нет, это верный ответ.
    console.error("[snapshot] 401: session already handled by fetchWithAuth");
    return { status: "empty" };
  }

  if (!res.ok) {
    console.error("[snapshot] error status:", res.status);
    return cachedOrUnavailable();
  }

  try {
    const data = (await res.json()) as AccessSnapshot;
    if (!data || typeof data !== "object") {
      console.error("[snapshot] 200 but body did not parse to an object");
      return { status: "empty" };
    }
    await cacheSnapshot(data);
    return { status: "ok", data };
  } catch (e) {
    console.error("[snapshot] 200 but json parse failed:", e instanceof Error ? e.message : String(e));
    return { status: "empty" };
  }
}

export function findEnrollment(
  snap: AccessSnapshot | null,
  courseId: number | string
): SnapshotEnrollment | null {
  if (!snap) return null;
  const cid = Number(courseId);
  return snap.enrollments.find((e) => e.course_id === cid) ?? null;
}

export function findSparks(
  snap: AccessSnapshot | null,
  saasClientId: string
): SnapshotSparks | null {
  if (!snap) return null;
  return snap.sparks.find((s) => s.saas_client_id === saasClientId) ?? null;
}
